import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Alert, Box, Typography,
  Tabs, Tab, TextField, Divider, Chip
} from '@mui/material';
import { CreditCard, Payment as PayPalIcon, AccountBalance } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements
} from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

const CARD_STYLE = {
  style: { base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } }, invalid: { color: '#9e2146' } }
};

const StripeCardForm: React.FC<{ invoice: any; tenantId: string; onSuccess: () => void; onError: (msg: string) => void }> = ({ invoice, tenantId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    try {
      const { data } = await api.post('/payments/stripe/intent', {
        amount: invoice.total,
        currency: 'USD',
        invoice_id: invoice.id || null,
        tenant_id: tenantId,
        description: `Payment — ${invoice.invoice_number || 'Balance Top-Up'}`,
      });
      const cardNumber = elements.getElement(CardNumberElement);
      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: { card: cardNumber! }
      });
      if (result.error) {
        onError(result.error.message || 'Payment failed');
      } else if (result.paymentIntent?.status === 'succeeded') {
        await api.post(`/payments/stripe/confirm/${result.paymentIntent.id}`);
        onSuccess();
      }
    } catch (e: any) {
      onError(e.response?.data?.message || e.message || 'Payment failed');
    } finally { setPaying(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paying <strong>${Number(invoice.total).toFixed(2)} USD</strong>
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Card Number</Typography>
        <CardNumberElement options={CARD_STYLE} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Expiry</Typography>
          <CardExpiryElement options={CARD_STYLE} />
        </Box>
        <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>CVC</Typography>
          <CardCvcElement options={CARD_STYLE} />
        </Box>
      </Box>
      <Button fullWidth variant="contained" size="large" onClick={pay} disabled={paying || !stripe}>
        {paying ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CreditCard sx={{ mr: 1 }} />}
        Pay ${Number(invoice.total).toFixed(2)}
      </Button>
    </Box>
  );
};

interface PaymentDialogProps {
  open: boolean;
  invoice: any;
  tenants?: any[];
  onClose: () => void;
  onSuccess: () => void;
}

export const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, invoice, tenants = [], onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const isCustomer = user?.role === 'customer_admin' || user?.role === 'customer_user';

  const [tab, setTab] = useState(0);
  const [gateways, setGateways] = useState<any>({ stripe: null, paypal: null });
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [manualForm, setManualForm] = useState({ method: 'bank_transfer', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Resolved tenant_id — use invoice.tenant_id if set, else fall back to the logged-in user's tenant
  const resolvedTenantId = invoice?.tenant_id || user?.tenant_id || '';

  useEffect(() => {
    if (!open) return;
    setSuccess(false); setError(''); setTab(0);
    api.get('/payments/gateways').then(({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      const s = rows.find((r: any) => r.gateway === 'stripe');
      const p = rows.find((r: any) => r.gateway === 'paypal');
      setGateways({ stripe: s, paypal: p });
      if (s?.enabled && s.config?.publishable_key) {
        setStripePromise(loadStripe(s.config.publishable_key));
      }
    }).catch(() => {});
  }, [open]);

  const handleSuccess = () => { setSuccess(true); setTimeout(() => { onSuccess(); onClose(); }, 2000); };
  const handleError = (msg: string) => setError(msg);

  const saveManual = async () => {
    setSaving(true); setError('');
    try {
      await api.post('/payments/manual', {
        invoice_id: invoice?.id || null,
        tenant_id: resolvedTenantId,
        amount: invoice?.total,
        currency: 'USD',
        method: manualForm.method,
        reference: manualForm.reference || null,
        notes: manualForm.notes || null,
        credit_balance: true,
      });
      handleSuccess();
    } catch (e: any) { setError(e.response?.data?.message || 'Payment recording failed'); }
    finally { setSaving(false); }
  };

  if (!invoice) return null;

  const hasStripe = gateways.stripe?.enabled && stripePromise;
  const hasPayPal = gateways.paypal?.enabled && gateways.paypal?.config?.client_id;
  const noGateways = !hasStripe && !hasPayPal;

  const paypalClientId = gateways.paypal?.config?.client_id || 'test';
  const paypalSandbox = gateways.paypal?.test_mode !== false;

  // Build tab list dynamically
  const tabs = [
    ...(hasStripe ? [{ label: 'Credit Card', icon: <CreditCard /> }] : []),
    ...(hasPayPal ? [{ label: 'PayPal', icon: <PayPalIcon /> }] : []),
    ...(!isCustomer ? [{ label: 'Manual / Bank', icon: <AccountBalance /> }] : []),
  ];

  // If customer and no gateways — show a single "Make Payment" tab that is bank-transfer based
  // but labelled as a request form
  const showBankRequestForCustomer = isCustomer && noGateways;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            {invoice.invoice_number === 'Balance Top-Up' ? 'Add Balance' : 'Pay Invoice'}
          </Typography>
          <Chip label={`$${Number(invoice.total).toFixed(2)} USD`} color="primary" />
        </Box>
        {invoice.invoice_number && (
          <Typography variant="body2" color="text.secondary">{invoice.invoice_number}</Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {success && <Alert severity="success" sx={{ mb: 2 }}>Payment successful! Your balance has been updated.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!success && (
          <>
            {/* No gateways configured — customer sees a bank details request */}
            {showBankRequestForCustomer && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Online payments (Stripe/PayPal) are being configured. Please make a bank transfer and submit the details below — your balance will be credited once confirmed.
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField fullWidth label="Your bank reference / transaction ID" value={manualForm.reference}
                    onChange={e => setManualForm({ ...manualForm, reference: e.target.value })}
                    placeholder="e.g. TXN-20240622-ABCDE" />
                  <TextField fullWidth multiline rows={2} label="Notes (optional)" value={manualForm.notes}
                    onChange={e => setManualForm({ ...manualForm, notes: e.target.value })}
                    placeholder="Any additional payment details..." />
                  <Button fullWidth variant="contained" size="large" onClick={saveManual}
                    disabled={saving || !manualForm.reference}>
                    {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Submit Payment Reference
                  </Button>
                </Box>
              </Box>
            )}

            {/* Tabs when at least one gateway is available, or admin */}
            {!showBankRequestForCustomer && tabs.length > 0 && (
              <>
                <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); }} sx={{ mb: 2 }}>
                  {tabs.map((t, i) => <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />)}
                </Tabs>

                {/* Stripe tab */}
                {hasStripe && tab === 0 && (
                  <Elements stripe={stripePromise}>
                    <StripeCardForm invoice={invoice} tenantId={resolvedTenantId} onSuccess={handleSuccess} onError={handleError} />
                  </Elements>
                )}

                {/* PayPal tab */}
                {hasPayPal && tab === (hasStripe ? 1 : 0) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Pay <strong>${Number(invoice.total).toFixed(2)} USD</strong> via PayPal
                    </Typography>
                    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD', intent: 'capture' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                        createOrder={async () => {
                          const { data } = await api.post('/payments/paypal/create-order', {
                            amount: invoice.total,
                            invoice_id: invoice.id || null,
                            tenant_id: resolvedTenantId,
                          });
                          return data.order_id;
                        }}
                        onApprove={async (data: any) => {
                          try {
                            await api.post(`/payments/paypal/capture/${data.orderID}`);
                            handleSuccess();
                          } catch (e: any) { handleError(e.response?.data?.message || 'Capture failed'); }
                        }}
                        onError={(err: any) => handleError('PayPal error: ' + JSON.stringify(err))}
                      />
                    </PayPalScriptProvider>
                  </Box>
                )}

                {/* Manual tab — admin only */}
                {!isCustomer && tab === tabs.length - 1 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Record a manual payment for ${Number(invoice.total).toFixed(2)} (bank transfer, cheque, etc.)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField select fullWidth label="Payment Method" value={manualForm.method}
                        onChange={e => setManualForm({ ...manualForm, method: e.target.value })}
                        SelectProps={{ native: true }}>
                        <option value="bank_transfer">Bank Transfer / Wire</option>
                        <option value="cheque">Cheque</option>
                        <option value="cash">Cash</option>
                        <option value="crypto">Crypto</option>
                      </TextField>
                      <TextField fullWidth label="Reference / Transaction ID" value={manualForm.reference}
                        onChange={e => setManualForm({ ...manualForm, reference: e.target.value })}
                        placeholder="Bank ref, cheque number, TxHash..." />
                      <TextField fullWidth multiline rows={2} label="Notes" value={manualForm.notes}
                        onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} />
                      <Button fullWidth variant="contained" size="large" onClick={saveManual} disabled={saving}>
                        {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                        Record Manual Payment
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* Admin sees no-gateway warning */}
            {!isCustomer && noGateways && tabs.length === 0 && (
              <Alert severity="warning">
                No payment gateways are enabled. Configure Stripe or PayPal in <strong>Payment Gateways</strong> settings.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{success ? 'Close' : 'Cancel'}</Button>
      </DialogActions>
    </Dialog>
  );
};
